/**
 * The cogent's cursor: a clump of its sand that travels from the orb to whatever it is working on, the grains trailing
 * behind as it moves. When it clicks, the target flashes orange and yellow and embers burst from the clump.
 */
import { emberSprites, sandSprites } from './grain'

const TAU = Math.PI * 2
const GRAINS = 130
const EMBER_GRAINS = 46
/** How fast the clump closes on its target: most of the way in about half a second. */
const LEAD_RATE = 6.5
const ARRIVED_PX = 8
const MAX_TRAVEL_MS = 1100
const FLASH_MS = 520
/** The click lands while the flash is near its brightest. */
const CLICK_AFTER_MS = 130
const LINGER_MS = 1600
const FADE_MS = 500
const DPR_MAX = 2
const OWN_ORB = '[data-cogent-orb]'

type Grain = {
  x: number
  y: number
  dx: number
  dy: number
  rate: number
  size: number
  alpha: number
  sprite: HTMLCanvasElement
  spin: number
}
type Ember = { angle: number; reach: number; size: number; sprite: HTMLCanvasElement }
type Flash = { el: HTMLElement; start: number; x: number; y: number; embers: Ember[] }
type Cursor = {
  canvas: HTMLCanvasElement
  context: CanvasRenderingContext2D
  grains: Grain[]
  x: number
  y: number
  target: HTMLElement | null
  arrived: (() => void) | null
  flashes: Flash[]
  lastUsed: number
  last: number
}

let cursor: Cursor | null = null

const still = () => matchMedia('(prefers-reduced-motion: reduce)').matches

function gaussian() {
  return Math.sqrt(-2 * Math.log(1 - Math.random())) * Math.cos(TAU * Math.random())
}

/** Where the cogent is on the page, so the clump leaves from it. The embed's orb sits in a shadow root. */
function home() {
  const orb =
    document.querySelector(OWN_ORB) ?? document.querySelector('[data-site-cogent]')?.shadowRoot?.querySelector(OWN_ORB)

  if (!orb) return { x: innerWidth - 70, y: innerHeight - 70 }

  const box = orb.getBoundingClientRect()
  return { x: box.left + box.width / 2, y: box.top + box.height / 2 }
}

function create(): Cursor {
  const canvas = document.createElement('canvas')
  canvas.setAttribute('aria-hidden', 'true')
  canvas.setAttribute('data-cogent-cursor', '')
  canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;z-index:2147483101;pointer-events:none'
  document.body.append(canvas)
  const sprites = sandSprites()
  const from = home()
  const grains = Array.from({ length: GRAINS }, (): Grain => {
    // Mostly a tight clump, with a few strays, as the orb has its dust.
    const stray = Math.random() < 0.12
    const spread = stray ? 15 : 6.5
    return {
      x: from.x,
      y: from.y,
      dx: gaussian() * spread,
      dy: gaussian() * spread,
      rate: LEAD_RATE * (0.75 + Math.random() * 1.1),
      size: (stray ? 0.8 : 1) * (1.5 + Math.random() * 1.2),
      alpha: stray ? 0.45 : 0.72 + Math.random() * 0.28,
      sprite: sprites[Math.floor(Math.random() * sprites.length)]!,
      spin: (Math.random() - 0.5) * 1.2,
    }
  })
  const now = performance.now()
  return {
    canvas,
    context: canvas.getContext('2d')!,
    grains,
    x: from.x,
    y: from.y,
    target: null,
    arrived: null,
    flashes: [],
    lastUsed: now,
    last: now,
  }
}

const centerOf = (el: HTMLElement) => {
  const box = el.getBoundingClientRect()
  return { x: box.left + box.width / 2, y: box.top + box.height / 2 }
}

function drawFlash(c: CanvasRenderingContext2D, flash: Flash, age: number, dpr: number, calm: boolean) {
  const progress = age / FLASH_MS
  const fade = (1 - progress) ** 2
  const box = flash.el.isConnected ? flash.el.getBoundingClientRect() : null

  if (box) {
    const grow = calm ? 4 : 4 + progress * 16
    const reach = Math.max(box.width, box.height) / 2 + 26 + grow
    const cx = box.left + box.width / 2
    const cy = box.top + box.height / 2
    const glow = c.createRadialGradient(cx * dpr, cy * dpr, 0, cx * dpr, cy * dpr, reach * dpr)
    glow.addColorStop(0, 'rgb(255 214 64 / 72%)')
    glow.addColorStop(0.55, 'rgb(255 150 36 / 38%)')
    glow.addColorStop(1, 'rgb(255 122 20 / 0%)')
    c.globalAlpha = fade
    c.fillStyle = glow
    c.fillRect((cx - reach) * dpr, (cy - reach) * dpr, reach * 2 * dpr, reach * 2 * dpr)
    c.strokeStyle = 'rgb(255 160 30)'
    c.lineWidth = 2.5 * dpr
    c.beginPath()
    c.roundRect(
      (box.left - grow) * dpr,
      (box.top - grow) * dpr,
      (box.width + grow * 2) * dpr,
      (box.height + grow * 2) * dpr,
      (8 + grow) * dpr
    )
    c.stroke()
  }

  const out = 1 - (1 - progress) ** 3

  for (const ember of flash.embers) {
    c.globalAlpha = fade
    const x = flash.x + Math.cos(ember.angle) * ember.reach * out
    const y = flash.y + Math.sin(ember.angle) * ember.reach * out
    const size = ember.size * dpr
    c.drawImage(ember.sprite, x * dpr - size / 2, y * dpr - size / 2, size, size)
  }
}

function draw(now: number) {
  const state = cursor

  if (!state) return

  const idle = now - state.lastUsed - LINGER_MS
  const working = state.target !== null && state.arrived !== null

  if (!working && !state.flashes.length && idle > FADE_MS) {
    state.canvas.remove()
    cursor = null
    return
  }

  const calm = still()
  const dt = Math.min(0.05, (now - state.last) / 1000)
  state.last = now
  const dpr = Math.min(DPR_MAX, devicePixelRatio || 1)
  const width = Math.round(innerWidth * dpr)
  const height = Math.round(innerHeight * dpr)
  const { canvas, context } = state

  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width
    canvas.height = height
  } else context.clearRect(0, 0, width, height)

  if (state.target?.isConnected) {
    const to = centerOf(state.target)
    const blend = calm ? 1 : 1 - Math.exp(-LEAD_RATE * dt)
    state.x += (to.x - state.x) * blend
    state.y += (to.y - state.y) * blend

    if (state.arrived && Math.hypot(to.x - state.x, to.y - state.y) < ARRIVED_PX) {
      state.arrived()
      state.arrived = null
    }
  } else if (state.arrived) {
    state.arrived()
    state.arrived = null
  }

  state.flashes = state.flashes.filter((flash) => now - flash.start < FLASH_MS)

  for (const flash of state.flashes) drawFlash(context, flash, now - flash.start, dpr, calm)

  const fading = idle > 0 ? Math.max(0, 1 - idle / FADE_MS) : 1

  for (const grain of state.grains) {
    const follow = calm ? 1 : 1 - Math.exp(-grain.rate * dt)
    // A slow swirl keeps the clump alive while it waits.
    const turn = calm ? 0 : grain.spin * (now / 1000)
    const cos = Math.cos(turn)
    const sin = Math.sin(turn)
    grain.x += (state.x + grain.dx * cos - grain.dy * sin - grain.x) * follow
    grain.y += (state.y + grain.dx * sin + grain.dy * cos - grain.y) * follow
    context.globalAlpha = grain.alpha * fading
    const size = grain.size * dpr
    context.drawImage(grain.sprite, grain.x * dpr - size / 2, grain.y * dpr - size / 2, size, size)
  }

  requestAnimationFrame(draw)
}

/** Sends the cursor to `el`, following it as the page moves. Resolves when it arrives. */
export function cursorTo(el: HTMLElement): Promise<void> {
  const fresh = !cursor
  const state = (cursor ??= create())
  state.arrived?.()
  state.target = el
  state.lastUsed = performance.now()

  if (fresh) requestAnimationFrame(draw)

  return new Promise((resolve) => {
    // A hidden tab draws no frames, and the action must not wait on one.
    const timeout = setTimeout(resolve, MAX_TRAVEL_MS)
    state.arrived = () => {
      clearTimeout(timeout)
      resolve()
    }
  })
}

/** Travels to `el` and activates it. Resolves at the moment the click should land. */
export async function cursorClick(el: HTMLElement) {
  await cursorTo(el)
  const state = cursor

  if (!state) return

  const sprites = emberSprites()
  const calm = still()
  state.lastUsed = performance.now()
  state.flashes.push({
    el,
    start: performance.now(),
    x: state.x,
    y: state.y,
    embers: calm
      ? []
      : Array.from({ length: EMBER_GRAINS }, () => ({
          angle: Math.random() * TAU,
          reach: 14 + Math.random() * 46,
          size: 1.6 + Math.random() * 1.6,
          sprite: sprites[Math.floor(Math.random() * sprites.length)]!,
        })),
  })
  await new Promise((resolve) => setTimeout(resolve, CLICK_AFTER_MS))
}
